/**
 * ExplorationZonesPage — J7-W6 (PRD §12.3.10 task 8 / §9.12.3).
 *
 * Lists every Exploration Zone in the active org. Columns: title, domain,
 * scope summary (truncated jsonb), expires_at (with days-remaining badge),
 * status badge, decision_note_id link.
 *
 * Filter chips: status (active / expired / revoked / all), domain.
 * "+ New Exploration Zone" CTA opens ExplorationZoneFormDialog.
 * Per-row actions: View, Revoke (active only — opens ConfirmDialog),
 * Close → Decision Note (active only — hands off to the J5
 * DecisionFormDialog with zone metadata pre-filled in CREATE mode).
 *
 * GOV-05 — every create/revoke is audit-logged via the audit-map (the W3/W4/W5
 * FK-safe `m.changedByUserId` shape).
 * GOV-01 — closing a zone produces a Decision Note referencing it (the
 * minimum-viable handoff is implemented; post-approval back-link to the zone's
 * decision_note_id is deferred to a follow-up since the backend doesn't
 * currently expose an "update decision_note_id" endpoint).
 */

import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ScrollText, Plus, Landmark } from 'lucide-react';
import { t } from '@/lib/i18n';
import { RorosPage } from '@/components/roros/RorosPage';
import { QueryPage } from '@/components/QueryPage';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { showToast } from '@/components/ui/Toast';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import {
  useRorosExplorationZones,
  useRorosExplorationZoneRevoke,
  useRorosExplorationZoneClose,
} from '@/hooks/roros/governance';
import { useRorosDomains } from '@/hooks/roros/domains';
import { formatDate } from '@/lib/localeFormatters';
import { ExplorationZoneFormDialog } from './ExplorationZoneFormDialog';
import { DecisionFormDialog } from '../decisions/DecisionFormDialog';
import type { RorosExplorationZoneDTO } from '@/services/roros/governance.service';
import type { RorosDomainDTO } from '@/types/roros/domains';

// URL key for FormSheet URL routing (AC-W4b-03).
const EXPLORATION_URL_KEY = 'edit-exploration';
// Stable ID used for create-mode URL routing (no item ID for a create-only form).
const EXPLORATION_CREATE_URL_ID = 'new';

// ─── Status badge ─────────────────────────────────────────────────────────────

function statusBadge(status: string): React.ReactNode {
  switch (status) {
    case 'active':
      return <Badge variant="success">{t('Active')}</Badge>;
    case 'expired':
      return <Badge variant="secondary">{t('Expired')}</Badge>;
    case 'revoked':
      return <Badge variant="error">{t('Revoked')}</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

// ─── Days-remaining badge ─────────────────────────────────────────────────────

function expiryBadge(zone: RorosExplorationZoneDTO): React.ReactNode {
  if (zone.status === 'revoked') {
    return <span className="text-xs text-muted-foreground">{t('Revoked')}</span>;
  }
  const expiresAt = new Date(zone.expiresAt).getTime();
  const now = Date.now();
  const daysRemaining = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));

  if (daysRemaining < 0 || zone.status === 'expired') {
    return (
      <Badge variant="error" data-testid="zone-expiry-badge-expired">
        {t('Expired')}
      </Badge>
    );
  }
  if (daysRemaining <= 7) {
    return (
      <Badge variant="warning" data-testid="zone-expiry-badge-soon">
        {daysRemaining} {t('days left')}
      </Badge>
    );
  }
  return (
    <Badge variant="success" data-testid="zone-expiry-badge-ok">
      {daysRemaining} {t('days left')}
    </Badge>
  );
}

// ─── Scope summary (truncated jsonb) ──────────────────────────────────────────

function scopeSummary(scope: Record<string, unknown> | null | undefined): string {
  if (!scope || Object.keys(scope).length === 0) return t('—');
  const keys = Object.keys(scope);
  const preview = keys.slice(0, 2).join(', ');
  return keys.length > 2 ? `${preview} (+${keys.length - 2})` : preview;
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function ExplorationZonesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [domainFilter, setDomainFilter] = useState<string>('all');
  const [isFormOpen, setIsFormOpen] = useState(() => searchParams.get(EXPLORATION_URL_KEY) === EXPLORATION_CREATE_URL_ID);
  const [revokeTarget, setRevokeTarget] = useState<RorosExplorationZoneDTO | null>(null);
  const [closeTarget, setCloseTarget] = useState<RorosExplorationZoneDTO | null>(null);

  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (statusFilter !== 'all') p.status = statusFilter;
    if (domainFilter !== 'all') p.domainId = domainFilter;
    return Object.keys(p).length > 0 ? p : undefined;
  }, [statusFilter, domainFilter]);

  const zonesQuery = useRorosExplorationZones(params);
  const domainsQuery = useRorosDomains();
  const revokeMutation = useRorosExplorationZoneRevoke();
  const closeMutation = useRorosExplorationZoneClose();

  const domainMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of (domainsQuery.data as RorosDomainDTO[] | undefined) ?? []) {
      m.set(d.id, d.name);
    }
    return m;
  }, [domainsQuery.data]);

  const domainOptions = useMemo(() => {
    return [
      { value: 'all', label: t('All domains') },
      ...((domainsQuery.data as RorosDomainDTO[] | undefined) ?? []).map((d) => ({
        value: d.id,
        label: d.name,
      })),
    ];
  }, [domainsQuery.data]);

  const handleRevokeConfirm = () => {
    if (!revokeTarget) return;
    revokeMutation.mutate(revokeTarget.id, {
      onSuccess: () => {
        showToast.success(t('Exploration zone revoked'));
        setRevokeTarget(null);
      },
      onError: (err: Error) => {
        showToast.error(err.message || t('Failed to revoke exploration zone'));
      },
    });
  };

  return (
    <RorosPage
      title={t('Exploration Zones')}
      icon={<Landmark className="h-5 w-5" aria-hidden />}
      actions={
        <Button
          onClick={() => setIsFormOpen(true)}
          leftIcon={<Plus className="h-4 w-4" />}
          data-testid="exploration-zones-new-button"
        >
          {t('New Exploration Zone')}
        </Button>
      }
    >
      <div className="mt-4 flex flex-wrap gap-3">
        <div data-testid="exploration-zones-status-filter">
          <Select
            label={t('Status')}
            value={statusFilter}
            onValueChange={setStatusFilter}
            options={[
              { value: 'all', label: t('All statuses') },
              { value: 'active', label: t('Active') },
              { value: 'expired', label: t('Expired') },
              { value: 'revoked', label: t('Revoked') },
            ]}
          />
        </div>
        <div data-testid="exploration-zones-domain-filter">
          <Select
            label={t('Domain')}
            value={domainFilter}
            onValueChange={setDomainFilter}
            options={domainOptions}
          />
        </div>
      </div>

      <div className="mt-6">
        <QueryPage
          query={zonesQuery}
          emptyMessage={t('No exploration zones yet. Create one to time-box a relaxed-governance experiment in a domain.')}
          emptyIcon={<ScrollText className="h-8 w-8" />}
          emptyAction={
            <Button onClick={() => setIsFormOpen(true)} leftIcon={<Plus className="h-4 w-4" />}>
              {t('Create your first exploration zone')}
            </Button>
          }
        >
          {(zones) => {
            const list = zones as RorosExplorationZoneDTO[];
            if (list.length === 0) {
              return (
                <div className="text-center text-sm text-muted-foreground py-8">
                  {t('No exploration zones match the current filters.')}
                </div>
              );
            }
            return (
              <div className="overflow-x-auto rounded-lg border border-border">
                <table
                  className="w-full min-w-[640px] text-sm"
                  aria-label={t('Exploration Zones')}
                  data-testid="exploration-zones-table"
                >
                  <thead>
                    <tr className="border-b border-border bg-muted/40">
                      <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5">{t('Title')}</th>
                      <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-32">{t('Domain')}</th>
                      <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-40">{t('Scope')}</th>
                      <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-28">{t('Expires')}</th>
                      <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-28">{t('Status')}</th>
                      <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-32">{t('Decision Note')}</th>
                      <th className="text-right text-xs font-semibold text-muted-foreground px-4 py-2.5 w-48">{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((z) => (
                      <tr
                        key={z.id}
                        className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors"
                        data-testid="exploration-zone-row"
                      >
                        <td className="px-4 py-3 font-medium text-foreground">{z.title}</td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {domainMap.get(z.domainId) ?? z.domainId.slice(0, 8)}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {scopeSummary(z.scope)}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          <div className="flex flex-col gap-1">
                            <span className="text-muted-foreground">
                              {z.expiresAt ? formatDate(new Date(z.expiresAt)) : '—'}
                            </span>
                            {expiryBadge(z)}
                          </div>
                        </td>
                        <td className="px-4 py-3">{statusBadge(z.status)}</td>
                        <td className="px-4 py-3 text-xs">
                          {z.decisionNoteId ? (
                            <Button
                              variant="link"
                              size="sm"
                              className="h-auto p-0"
                              onClick={() => navigate(`/roros/decisions?openId=${z.decisionNoteId}`)}
                              data-testid="exploration-zone-decision-link"
                            >
                              {t('View')}
                            </Button>
                          ) : (
                            <span className="text-muted-foreground">{t('—')}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/roros/governance/exploration-zones/${z.id}`)}
                              data-testid="exploration-zone-view-button"
                            >
                              {t('View')}
                            </Button>
                            {z.status === 'active' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setCloseTarget(z)}
                                  data-testid="exploration-zone-close-button"
                                >
                                  {t('Close → Decision Note')}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setRevokeTarget(z)}
                                  data-testid="exploration-zone-revoke-button"
                                >
                                  {t('Revoke')}
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }}
        </QueryPage>
      </div>

      {/* AC-W4b-03: FormSheet with URL routing; urlKey/urlId enable browser-back-to-close */}
      <ExplorationZoneFormDialog
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onCreated={() => zonesQuery.refetch()}
        urlKey={EXPLORATION_URL_KEY}
        urlId={EXPLORATION_CREATE_URL_ID}
      />

      <ConfirmDialog
        isOpen={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onConfirm={handleRevokeConfirm}
        title={t('Revoke this exploration zone?')}
        description={t('Revoking immediately ends the zone. This cannot be undone (GOV-05 audit event recorded).')}
        variant="destructive"
        confirmText={t('Revoke')}
        isLoading={revokeMutation.isPending}
      />

      {/* GOV-01 — Close → Decision Note handoff. On Decision Note creation the
          close endpoint atomically back-links the note and sets status='revoked'. */}
      <DecisionFormDialog
        isOpen={!!closeTarget}
        onClose={() => setCloseTarget(null)}
        defaultImpact="trunk"
        onCreated={(decisionId) => {
          if (closeTarget && decisionId) {
            closeMutation.mutate(
              { id: closeTarget.id, decisionNoteId: decisionId },
              {
                onSuccess: () => {
                  showToast.success(t('Zone closed and linked to Decision Note.'));
                  setCloseTarget(null);
                  zonesQuery.refetch();
                },
                onError: (err: any) => {
                  showToast.error(err?.message ?? t('Failed to back-link zone to Decision Note.'));
                  setCloseTarget(null);
                  zonesQuery.refetch();
                },
              },
            );
          } else {
            // Fallback — Decision Note created but id missing; tell user to revoke manually
            showToast.info(t('Decision Note created. Revoke the zone manually if needed.'));
            setCloseTarget(null);
            zonesQuery.refetch();
          }
        }}
      />
    </RorosPage>
  );
}
